import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { fg } from '@atlaskit/platform-feature-flags';
import { isContainedWithinMediaWrapper } from '../../vc-observer/media-wrapper/vc-utils';
import isNonVisualStyleMutation from '../../vc-observer/observers/non-visual-styles/is-non-visual-style-mutation';
import { RLLPlaceholderHandlers } from '../../vc-observer/observers/rll-placeholders';
import { createIntersectionObserver } from './intersection-observer';
import createMutationObserver from './mutation-observer';
import createPerformanceObserver from './performance-observer';
import checkCssProperty from './utils/check-display-content';
import checkWithinComponent, { cleanupCaches } from './utils/check-within-component';
import isInVCIgnoreIfNoLayoutShiftMarker from './utils/is-in-vc-ignore-if-no-layout-shift-marker';
import trackDisplayContentsOccurrence from './utils/track-display-content-occurrence';
function isElementVisible(element) {
  if (!(element instanceof HTMLElement)) {
    return true;
  }
  try {
    const visible = element.checkVisibility({
      // @ts-ignore - visibilityProperty may not exist in all TS environments
      visibilityProperty: true,
      contentVisibilityAuto: true,
      opacityProperty: true
    });
    return visible;
  } catch (e) {
    // there is no support for checkVisibility
    return true;
  }
}
function sameRectSize(a, b) {
  if (!a || !b) {
    return false;
  }
  return a.width === b.width && a.height === b.height;
}
function sameRectDimensions(a, b) {
  if (!a || !b) {
    return false;
  }
  return a.width === b.width && a.height === b.height && a.x === b.x && a.y === b.y;
}
const createElementMutationsWatcher = removedNodeRects => ({
  target,
  rect
}) => {
  const isInIgnoreLsMarker = isInVCIgnoreIfNoLayoutShiftMarker(target);
  if (!isInIgnoreLsMarker) {
    return 'mutation:element';
  }
  const isRLLPlaceholder = RLLPlaceholderHandlers.getInstance().isRLLPlaceholderHydration(rect);
  if (isRLLPlaceholder && isInIgnoreLsMarker) {
    return 'mutation:rll-placeholder';
  }
  const wasDeleted = removedNodeRects.some(nr => sameRectDimensions(nr, rect));
  if (wasDeleted && isInIgnoreLsMarker) {
    return 'mutation:element-replacement';
  }
  return 'mutation:element';
};
export default class ViewportObserver {
  // SSR context functions

  constructor({
    onChange,
    getSSRState,
    getSSRPlaceholderHandler
  }) {
    _defineProperty(this, "handleIntersectionEntry", ({
      target,
      rect,
      time,
      type,
      mutationData
    }) => {
      if (!target) {
        return;
      }
      const visible = isElementVisible(target);
      const lastElementRect = this.mapVisibleNodeRects.get(target);
      this.mapVisibleNodeRects.set(target, rect);
      this.onChange({
        time,
        type,
        elementRef: new WeakRef(target),
        visible,
        rect,
        previousRect: lastElementRect,
        mutationData
      });
    });
    _defineProperty(this, "handleChildListMutation", async ({
      target,
      addedNodes,
      removedNodes,
      timestamp
    }) => {
      const removedNodeRects = removedNodes.map(ref => {
        const n = ref.deref();
        if (!n) {
          return;
        }
        return this.mapVisibleNodeRects.get(n);
      });
      const targetNode = target.deref();
      for (const addedNodeRef of addedNodes) {
        const addedNode = addedNodeRef.deref();
        if (!addedNode) {
          continue;
        }

        // SSR hydration logic
        if (this.getSSRState) {
          const ssrState = this.getSSRState();
          const SSRStateEnum = {
            normal: 1,
            waitingForFirstRender: 2,
            ignoring: 3
          };
          if (ssrState.state === SSRStateEnum.waitingForFirstRender && timestamp > ssrState.renderStart && targetNode === ssrState.reactRootElement) {
            var _this$intersectionObs;
            ssrState.state = SSRStateEnum.ignoring;
            if (ssrState.renderStop === -1) {
              // arbitrary 500ms DOM update window
              ssrState.renderStop = timestamp + 500;
            }
            (_this$intersectionObs = this.intersectionObserver) === null || _this$intersectionObs === void 0 ? void 0 : _this$intersectionObs.watchAndTag(addedNode, 'ssr-hydration');
            continue;
          }
          if (ssrState.state === SSRStateEnum.ignoring && timestamp > ssrState.renderStart && targetNode === ssrState.reactRootElement) {
            if (timestamp <= ssrState.renderStop) {
              var _this$intersectionObs2;
              (_this$intersectionObs2 = this.intersectionObserver) === null || _this$intersectionObs2 === void 0 ? void 0 : _this$intersectionObs2.watchAndTag(addedNode, 'ssr-hydration');
              continue;
            } else {
              ssrState.state = SSRStateEnum.normal;
            }
          }
        }

        // SSR placeholder logic - check and handle with await
        if (this.getSSRPlaceholderHandler) {
          const ssrPlaceholderHandler = this.getSSRPlaceholderHandler();
          if (ssrPlaceholderHandler) {
            if (ssrPlaceholderHandler.isPlaceholder(addedNode) || ssrPlaceholderHandler.isPlaceholderIgnored(addedNode)) {
              if (fg('platform_ufo_ssr_placeholder_resolution_ttvc_v3')) {
                if (ssrPlaceholderHandler.checkIfExistedAndSizeMatchingV3(addedNode)) {
                  var _this$intersectionObs3;
                  (_this$intersectionObs3 = this.intersectionObserver) === null || _this$intersectionObs3 === void 0 ? void 0 : _this$intersectionObs3.watchAndTag(addedNode, 'mutation:ssr-placeholder');
                  continue;
                }
              } else {
                const result = await ssrPlaceholderHandler.checkIfExistedAndSizeMatching(addedNode);
                if (result !== false) {
                  var _this$intersectionObs4;
                  (_this$intersectionObs4 = this.intersectionObserver) === null || _this$intersectionObs4 === void 0 ? void 0 : _this$intersectionObs4.watchAndTag(addedNode, 'mutation:ssr-placeholder');
                  continue;
                }
              }
              // If result is false, continue to normal mutation logic below
            }
            if (ssrPlaceholderHandler.isPlaceholderReplacement(addedNode) || ssrPlaceholderHandler.isPlaceholderIgnored(addedNode)) {
              const result = await ssrPlaceholderHandler.validateReactComponentMatchToPlaceholder(addedNode);
              if (result !== false) {
                var _this$intersectionObs5;
                (_this$intersectionObs5 = this.intersectionObserver) === null || _this$intersectionObs5 === void 0 ? void 0 : _this$intersectionObs5.watchAndTag(addedNode, 'mutation:ssr-placeholder');
                continue;
              }
              // If result is false, continue to normal mutation logic below
            }
          }
        }
        const sameDeletedNode = removedNodes.find(ref => {
          const n = ref.deref();
          if (!n || !addedNode) {
            return false;
          }
          return n.isEqualNode(addedNode);
        });
        const isInIgnoreLsMarker = isInVCIgnoreIfNoLayoutShiftMarker(addedNode);
        if (sameDeletedNode && isInIgnoreLsMarker) {
          var _this$intersectionObs6;
          (_this$intersectionObs6 = this.intersectionObserver) === null || _this$intersectionObs6 === void 0 ? void 0 : _this$intersectionObs6.watchAndTag(addedNode, 'mutation:remount');
          continue;
        }
        if (isContainedWithinMediaWrapper(addedNode)) {
          var _this$intersectionObs7;
          (_this$intersectionObs7 = this.intersectionObserver) === null || _this$intersectionObs7 === void 0 ? void 0 : _this$intersectionObs7.watchAndTag(addedNode, 'mutation:media');
          continue;
        }
        const {
          isWithin: isWithinThirdPartySegment
        } = checkWithinComponent(addedNode, 'UFOThirdPartySegment', this.mapIs3pResult);
        if (isWithinThirdPartySegment) {
          var _this$intersectionObs8;
          (_this$intersectionObs8 = this.intersectionObserver) === null || _this$intersectionObs8 === void 0 ? void 0 : _this$intersectionObs8.watchAndTag(addedNode, 'mutation:third-party-element');
          continue;
        }
        if (fg('platform_ufo_display_content_resolution_ttvc_v3')) {
          // Check if the target has display:content css property, return array of valid targets
          const validTargets = checkCssProperty(addedNode);
          for (const validTarget of validTargets) {
            var _this$intersectionObs9;
            (_this$intersectionObs9 = this.intersectionObserver) === null || _this$intersectionObs9 === void 0 ? void 0 : _this$intersectionObs9.watchAndTag(validTarget, createElementMutationsWatcher(removedNodeRects));
          }
        } else {
          var _this$intersectionObs0;
          if (fg('platform_ufo_display_content_track_occurrence')) {
            trackDisplayContentsOccurrence(addedNode);
          }
          (_this$intersectionObs0 = this.intersectionObserver) === null || _this$intersectionObs0 === void 0 ? void 0 : _this$intersectionObs0.watchAndTag(addedNode, createElementMutationsWatcher(removedNodeRects));
        }
      }
    });
    _defineProperty(this, "handleAttributeMutation", ({
      target,
      attributeName,
      oldValue,
      newValue
    }) => {
      var _this$intersectionObs1;
      (_this$intersectionObs1 = this.intersectionObserver) === null || _this$intersectionObs1 === void 0 ? void 0 : _this$intersectionObs1.watchAndTag(target, ({
        target,
        rect
      }) => {
        if (isContainedWithinMediaWrapper(target)) {
          return {
            type: 'mutation:media',
            mutationData: {
              attributeName,
              oldValue,
              newValue
            }
          };
        }
        if (isNonVisualStyleMutation({
          target,
          attributeName,
          type: 'attributes'
        })) {
          return {
            type: 'mutation:attribute:non-visual-style',
            mutationData: {
              attributeName,
              oldValue,
              newValue
            }
          };
        }
        const isRLLPlaceholder = RLLPlaceholderHandlers.getInstance().isRLLPlaceholderHydration(rect);
        if (isRLLPlaceholder) {
          return {
            type: 'mutation:rll-placeholder',
            mutationData: {
              attributeName,
              oldValue,
              newValue
            }
          };
        }
        const lastElementRect = this.mapVisibleNodeRects.get(target);
        if (lastElementRect && sameRectSize(rect, lastElementRect)) {
          return {
            type: 'mutation:attribute:no-layout-shift',
            mutationData: {
              attributeName,
              oldValue,
              newValue
            }
          };
        }
        return {
          type: 'mutation:attribute',
          mutationData: {
            attributeName,
            oldValue,
            newValue
          }
        };
      });
      // }
    });
    _defineProperty(this, "handleLayoutShift", ({
      time,
      changedRects
    }) => {
      for (const changedRect of changedRects) {
        const target = changedRect.node;
        if (target) {
          this.onChange({
            time,
            elementRef: new WeakRef(target),
            visible: true,
            rect: changedRect.rect,
            previousRect: changedRect.previousRect,
            type: 'layout-shift'
          });
        }
      }
    });
    this.mapVisibleNodeRects = new WeakMap();
    this.mapIs3pResult = new WeakMap();
    this.onChange = onChange;
    this.isStarted = false;
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.performanceObserver = null;

    // Initialize SSR context functions
    this.getSSRState = getSSRState;
    this.getSSRPlaceholderHandler = getSSRPlaceholderHandler;
  }
  initializeObservers() {
    if (this.isStarted) {
      return;
    }
    this.intersectionObserver = createIntersectionObserver({
      onEntry: this.handleIntersectionEntry
    });
    this.mutationObserver = createMutationObserver({
      onChildListMutation: this.handleChildListMutation,
      onAttributeMutation: this.handleAttributeMutation
    });
    this.performanceObserver = createPerformanceObserver({
      onLayoutShift: this.handleLayoutShift
    });
  }
  start() {
    var _this$mutationObserve, _this$performanceObse;
    if (this.isStarted) {
      return;
    }
    this.initializeObservers();
    (_this$mutationObserve = this.mutationObserver) === null || _this$mutationObserve === void 0 ? void 0 : _this$mutationObserve.observe(document.body, {
      attributeOldValue: true,
      attributes: true,
      childList: true,
      subtree: true
    });
    (_this$performanceObse = this.performanceObserver) === null || _this$performanceObse === void 0 ? void 0 : _this$performanceObse.observe({
      type: 'layout-shift',
      buffered: true,
      // @ts-ignore -error
      durationThreshold: 30
    });
    this.isStarted = true;
  }
  stop() {
    var _this$mutationObserve2, _this$intersectionObs10, _this$performanceObse2;
    if (!this.isStarted) {
      return;
    }
    (_this$mutationObserve2 = this.mutationObserver) === null || _this$mutationObserve2 === void 0 ? void 0 : _this$mutationObserve2.disconnect();
    (_this$intersectionObs10 = this.intersectionObserver) === null || _this$intersectionObs10 === void 0 ? void 0 : _this$intersectionObs10.disconnect();
    (_this$performanceObse2 = this.performanceObserver) === null || _this$performanceObse2 === void 0 ? void 0 : _this$performanceObse2.disconnect();
    this.isStarted = false;
    // Clean up caches when stopping
    cleanupCaches(this.mapIs3pResult);
  }
}